import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Tabs,
	AlertDialog,
	ConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../SettingsPage";
import {
	deleteUserRequest,
	listAllUserRequests,
	UserRequestClientModel,
	UserRequestRespondedClientModel,
	UserRequestRespondKind
} from "client/Api/UserRequests/manageUserRequests";
import { UserRequestStatus } from "client/Api/UserRequests/userRequest";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	UserRequestsTabContent,
	UserRequestGridItem,
	convertToGridItem
} from "./UserRequestsTabContent";
import {
	listContextualActions,
	getSelectionActions,
	ManageUserRequestsAction,
	ManageUserRequestActionContext
} from "./actions";
import { UserRequestDetailsDialog } from "./UserRequestDetailsDialog";
import { RespondToUserRequestDialog } from "./RespondToUserRequestDialog";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { never } from "client_legacy/ndaw/errors";
import { buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

const {
	Tab
} = Tabs;

const OPEN_TAB_ID = "open-requests";
const ACCEPTED_TAB_ID = "accepted-requests";
const DECLINED_TAB_ID = "declined-requests";

type TabId = typeof OPEN_TAB_ID | typeof ACCEPTED_TAB_ID | typeof DECLINED_TAB_ID;

export const ManageUserRequestsPage = (): ReactElement => {

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(false);
	const [gridItems, setGridItems] = useState<readonly UserRequestGridItem[]>([]);
	const [openSelection, setOpenSelection] = useState<readonly UserRequestGridItem[]>([]);
	const [acceptedSelection, setAcceptedSelection] = useState<readonly UserRequestGridItem[]>([]);
	const [declinedSelection, setDeclinedSelection] = useState<readonly UserRequestGridItem[]>([]);
	const [activeTabId, setActiveTabId] = useState<TabId>(OPEN_TAB_ID);
	const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
	const [itemsToBeDeleted, setItemsToBeDeleted] = useState<readonly UserRequestClientModel[]>();
	const [openedItem, setOpenedItem] = useState<UserRequestClientModel | undefined>();
	const [pendingResponse, setPendingResponse] = useState<{
		kind: UserRequestRespondKind;
		ids: readonly number[];
	} | undefined>();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	useActiveSettingsMenuItem(SettingsPageKind.UserRequests);

	const deselectHandler = useCallback(
		(): void => {
			switch (activeTabId)
			{
				case OPEN_TAB_ID:
					setOpenSelection([]);
					break;
				case ACCEPTED_TAB_ID:
					setAcceptedSelection([]);
					break;
				case DECLINED_TAB_ID:
					setDeclinedSelection([]);
					break;
				default:
					never(activeTabId);
			}
		},
		[activeTabId]
	);

	const [
		openItems,
		acceptedItems,
		declinedItems
	] = useMemo(
		(): [
				open: readonly UserRequestGridItem[],
				accepted: readonly UserRequestGridItem[],
				declined: readonly UserRequestGridItem[]
		] => {
			const groupedItems: Partial<Record<UserRequestStatus, UserRequestGridItem[]>> =
				groupBy(gridItems, x => x.request.status);

			return [
				groupedItems[UserRequestStatus.Open] ?? [],
				groupedItems[UserRequestStatus.Accepted] ?? [],
				groupedItems[UserRequestStatus.Declined] ?? [],
			];
		},
		[gridItems]
	);

	const selection: readonly UserRequestGridItem[] = useMemo(
		() => {
			switch (activeTabId)
			{
				case OPEN_TAB_ID:
					return openSelection;
				case ACCEPTED_TAB_ID:
					return acceptedSelection;
				case DECLINED_TAB_ID:
					return declinedSelection;
				default:
					console.warn(`'${activeTabId}' is an unknown tab`);
					return [];
			}
		},
		[
			activeTabId,
			acceptedSelection,
			declinedSelection,
			openSelection
		]
	);

	const contextualActions: readonly ManageUserRequestsAction[] = useMemo(
		() => {
			const openHandler = (request: UserRequestClientModel): void => {
				setOpenedItem(request);
			};

			const acceptHandler = (requests: readonly UserRequestClientModel[]): void => {
				setPendingResponse({
					kind: UserRequestRespondKind.Accept,
					ids: requests.map(x => x.id)
				});
			};

			const declineHandler = (requests: readonly UserRequestClientModel[]): void => {
				setPendingResponse({
					kind: UserRequestRespondKind.Decline,
					ids: requests.map(x => x.id)
				});
			};

			const deleteHandler = (requests: readonly UserRequestClientModel[]): void => {
				setItemsToBeDeleted(requests);
			};

			return listContextualActions({
				openHandler,
				acceptHandler,
				declineHandler,
				deleteHandler
			});
		},
		[]
	);

	const selectionActions: readonly ManageUserRequestsAction[] = useMemo(
		() => getSelectionActions({ deselectHandler }),
		[deselectHandler]
	);

	const actionContext = useMemo(
		(): ManageUserRequestActionContext => ({
			isMobile,
			selection: selection.map(x => x.request)
		}),
		[isMobile, selection]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ManageUserRequestsAction[] => {
			return buildActionsForTopBar<ManageUserRequestActionContext, ManageUserRequestsAction>(
				{
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	useEffect(
		() => {
			const loadViewModel = async (signal: AbortSignal): Promise<void> => {
				setIsInteractingWithTheBackend(true);
				try {
					const requests: readonly UserRequestClientModel[] = await listAllUserRequests(signal)
						.catch(handlePageDataLoadingError);

					setGridItems(requests.map(convertToGridItem));
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
				} finally {
					if (!signal.aborted) {
						setIsInteractingWithTheBackend(false);
					}
				}
			};

			const cancellation = new AbortController();
			loadViewModel(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const confirmDeleteHandler = async (items: readonly UserRequestClientModel[]): Promise<void> => {
		try {
			setIsInteractingWithTheBackend(true);
			const ids: readonly number[] = items.map(x => x.id);

			await deleteUserRequest(ids);

			setGridItems(
				(x) => x.filter(x => !ids.some(i => i === x.id))
			);
			alertManager.showSuccess(localize("_SuccessfullyDeletedTheRequests"));
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		} finally {
			setIsInteractingWithTheBackend(false);
			setItemsToBeDeleted(undefined);
		}
	};

	const respondedHandler = (response: readonly UserRequestRespondedClientModel[]): void => {
		setOpenedItem(undefined);
		setPendingResponse(undefined);

		setGridItems(
			(x) => {
				const copy: UserRequestGridItem[] = [...x];
				for (const responseItem of response)
				{
					const index: number = copy.findIndex(x => x.id === responseItem.id);
					if (index === -1) {
						console.warn(`GridItem with id '${responseItem.id}' does not exist, refresh the page`);
						continue;
					}
					const patchedRequest: UserRequestClientModel = {
						...copy[index].request,
						...responseItem
					};

					const newGridItem: UserRequestGridItem = convertToGridItem(patchedRequest);
					copy.splice(index, 1, newGridItem);
				}

				return copy;
			}
		);
	};

	const activeTabIdChangeHandler = useCallback(
		(tabId: string): void => {
			switch (tabId) {
				case "open-requests":
				case "accepted-requests":
				case "declined-requests":
					setActiveTabId(tabId);
					break;
				default:
					throw new Error(`Unknown tabId "${tabId}"`);
			}
		},
		[]
	);

	const openedItemCloseHandler = useCallback(
		(): void => {
			setOpenedItem(undefined);
		},
		[]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isInteractingWithTheBackend}
			actions={topBarActions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			title={localize("_UserRequests")}
			onMoreInfo={() => setShowMoreInfo(true)}
		>
			<SettingsPage.Section height="stretch">
				{gridItems.length === 0
					? <span>{localize("_CurrentlyThereAreNoRequests")}</span>
					: (
						<Tabs
							activeTabId={activeTabId}
							onActiveTabIdChange={activeTabIdChangeHandler}
							height="fill"
						>
							<Tab
								id={OPEN_TAB_ID}
								title={localize("_OpenRequests")}
							>
								<UserRequestsTabContent
									kind={UserRequestStatus.Open}
									items={openItems}
									isMobile={isMobile}
									selection={openSelection}
									onSelectionChange={setOpenSelection}
									actions={contextualActions}
									timezone={userTimezone}
								/>
							</Tab>

							<Tab
								id={ACCEPTED_TAB_ID}
								title={localize("_AcceptedRequests")}
							>
								<UserRequestsTabContent
									kind={UserRequestStatus.Accepted}
									items={acceptedItems}
									isMobile={isMobile}
									selection={acceptedSelection}
									onSelectionChange={setAcceptedSelection}
									actions={contextualActions}
									timezone={userTimezone}
								/>
							</Tab>

							<Tab
								id={DECLINED_TAB_ID}
								title={localize("_DeclinedRequests")}
							>
								<UserRequestsTabContent
									kind={UserRequestStatus.Declined}
									items={declinedItems}
									isMobile={isMobile}
									selection={declinedSelection}
									onSelectionChange={setDeclinedSelection}
									actions={contextualActions}
									timezone={userTimezone}
								/>
							</Tab>
						</Tabs>
					)
				}
			</SettingsPage.Section>

			{ showMoreInfo && (
				<AlertDialog
					title={localize("_UserRequests")}
					showCloseButton
					onClose={() => setShowMoreInfo(false)}
					trackingId="user-request-info-dialog"
				>
					{localize("_ManageUserRequestsInfoBody")}
				</AlertDialog>
			)}

			{ itemsToBeDeleted && (
				<ConfirmationDialog
					title={localize("_AreYouSureYouWantToDeleteThisRequests")}
					confirmText={localize("_Delete")}
					onConfirmation={() => confirmDeleteHandler(itemsToBeDeleted)}
					onCancellation={() => setItemsToBeDeleted(undefined)}
					trackingId="delete-user-requests-confirmation-dialog"
				/>
			)}

			{ openedItem && (
				<UserRequestDetailsDialog
					id={openedItem.id}
					onClose={openedItemCloseHandler}
					onResponded={respondedHandler}
					isOpenedFromOverview
				/>
			)}

			{ pendingResponse && (
				<RespondToUserRequestDialog
					ids={pendingResponse.ids}
					kind={pendingResponse.kind}
					onClose={() => setPendingResponse(undefined)}
					onResponded={respondedHandler}
				/>
			)}
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
ManageUserRequestsPage.displayName = "ManageUserRequestsPage";

export const manageUserRequestsRouting = {
	page: "/UserRequests/Manage"
};